import { useTranslation } from "react-i18next";
import { Button, Spinner } from '@shopify/polaris'
import React, { useEffect, useState, useMemo, useRef } from 'react'
import './style.css'
import { useSelector } from 'react-redux'
import useAppDispatch from '../../hooks/useAppDispatch'
import { fetchCompanyApp } from '../../redux/features/profile/profileSlice'
import { switchApp } from '../../redux/features/user/userSlice'
import { getAppIdFromStorage, isLocalhost } from '../../utils/appIdStorage'
import { getProjectsListForNavigation, getProjectsForSwitcher } from '../../projectConfig/projects'
import { buildProjectSwitchUrl } from '../../utils/workspaceSwitch/projectSwitchLanding'
import { switchBackFromSandbox } from '../../containers/ProductSettingsPage/Sandbox/model/sandboxSlice'
import { clearSandboxAccessRecoveryMark } from '../../containers/ProductSettingsPage/Sandbox/model/sandboxSession'

const ProjectNavigationWindow = (props) => {
	const dispatch = useAppDispatch()
	const { t } = useTranslation()
	const { companyApp, fetchingCompanyApp, fetchingCompanyAppError } = useSelector((state) => state.profile)
	const { openProjectName, loadingAppError } = useSelector(state => state.user)
	const app_id = getAppIdFromStorage()
	const [selectedApp, setSelectedApp] = useState(null)
	const [switchingBack, setSwitchingBack] = useState(false)
	const [switchBackError, setSwitchBackError] = useState("")
	const hasFetchedCompanyAppRef = useRef(false)

	const allProjectsList = useMemo(() => getProjectsListForNavigation(), [])
	const projectsForSwitcher = useMemo(() => getProjectsForSwitcher(), [])
	const accessError = loadingAppError || fetchingCompanyAppError

	useEffect(() => {
		if (hasFetchedCompanyAppRef.current || loadingAppError) return
		hasFetchedCompanyAppRef.current = true
		dispatch(fetchCompanyApp())
	}, [dispatch, loadingAppError])

	// Normalize /company/app response: can be array ["1", "2"] or object { 0: "1", 1: "2" }
	const selectedIds = useMemo(() => {
		if (!companyApp) return []
		const list = Array.isArray(companyApp) ? companyApp : Object.values(companyApp)
		return list.map(id => Number(id)).filter(n => !Number.isNaN(n))
	}, [companyApp])

	// Show only apps the company has access to (exactly what server sent in /company/app — no extra items)
	const isLoadingApps = fetchingCompanyApp === true
	const displayArr = useMemo(() => {
		if (isLoadingApps) return []
		return allProjectsList.filter(item => selectedIds.includes(item.id))
	}, [allProjectsList, selectedIds, isLoadingApps])

	// If app_id !== 0, automatically select project
	useEffect(() => {
		if (app_id !== 0 && openProjectName) {
			setSelectedApp(openProjectName)
		}
	}, [openProjectName, app_id])

	// By default, highlight the first element if nothing is selected
	useEffect(() => {
		if (displayArr.length > 0 && !selectedApp) {
			setSelectedApp(displayArr[0].value)
		}
	}, [displayArr, selectedApp])

	const changeProjectHandler = async () => {
		// Find selected project in switcher projects (has href and appId)
		const selectedProject = projectsForSwitcher.find(project =>
			selectedApp && selectedApp.toLowerCase() === project.name.toLowerCase()
		)
		
		if (!selectedProject) {
			alert(t("menu.selectValidApp"))
			return
		}
		
		try {
			// Wait for switchApp to complete before redirecting (useAppDispatch already unwraps, so no .unwrap() here)
			const result = await dispatch(switchApp(selectedProject.appId))
			if (result?.error) {
				alert(result.error === 'Unauthorized' ? t("menu.sessionExpiredLoginAgain") : result.error)
				return
			}
			// Save app_id to localStorage (only if not localhost)
			if (!isLocalhost()) {
				localStorage.setItem('app_id', selectedProject.appId.toString())
			}
			// Redirect after successful switch (settings hub: not every project has the current page)
			window.location.href = buildProjectSwitchUrl(selectedProject.href)
		} catch (e) {
			console.error('Error switching app:', e)
			const message = e?.payload || e?.message || t("menu.failedToSwitchApp")
			alert(message)
		}
	}

	const handleSwitchBackToLive = async () => {
		setSwitchingBack(true)
		setSwitchBackError("")
		try {
			const result = await dispatch(switchBackFromSandbox())
			if (result?.error) {
				setSwitchingBack(false)
					setSwitchBackError(
					result.error === "Unauthorized"
						? t("menu.sessionExpiredLoginAgain")
						: result.error
				)
				return
			}
			clearSandboxAccessRecoveryMark()
			window.location.reload()
		} catch (error) {
			setSwitchingBack(false)
			setSwitchBackError(
				error?.message || error?.payload?.message || t("menu.switchBackToLiveFailed")
			)
		}
	}

	return (
			<div className='topbar-wrapper_block-modal'>
				<div className='topbar-wrapper_block-modal_wrapper'>
					{accessError ? (
						<>
							<h3 className='topbar-wrapper_block-modal_wrapper-title'>
								{t("menu.workspaceAccessErrorTitle")}
							</h3>
							<p className='topbar-wrapper_block-modal_wrapper-subtitle'>
								{switchBackError || accessError}
							</p>
							<Button
								onClick={handleSwitchBackToLive}
								variant='primary'
								fullWidth
								loading={switchingBack}
							>
								{t("menu.switchBackToLive")}
							</Button>
						</>
					) : (
					<>
					<h3 className='topbar-wrapper_block-modal_wrapper-title'>
						{t("menu.projectPickerTitle")}
					</h3 >
					<p className='topbar-wrapper_block-modal_wrapper-subtitle'>
						{t("menu.projectPickerSubtitle")}
					</p >
					{isLoadingApps ? (
						<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 0', gap: 16 }}>
							<Spinner accessibilityLabel={t("menu.projectPickerLoadingLabel")} size="large" />
							<span style={{ color: '#707070', fontSize: 14 }}>{t("menu.projectPickerLoading")}</span>
						</div>
					) : (
						<>
							<ul className='topbar-wrapper_block-modal_wrapper-list'>
								{
									displayArr?.map((item, index) => (
										<li
											className={`topbar-wrapper_block-modal_wrapper-list_item ${selectedApp === item.value ? 'selected-line' : ''}`}
											key={item.id ?? index}
											onClick={() => setSelectedApp(item.value)}
										>
											<img
												src={item?.img}
												alt={item?.label}
												className='topbar-wrapper_block-modal_wrapper-list_item-icon'
											/>
											<div style={{ marginLeft: '10px', paddingRight: 10 }}>
												<h3 className='topbar-wrapper_block-modal_wrapper-list_item-title'>{item.label}</h3>
												{selectedApp === item.value && item.description ? (
													<p className='topbar-wrapper_block-modal_wrapper-list_item-descriptoin'>
														{item.descriptionKey ? t(item.descriptionKey, { defaultValue: item.description }) : item.description}
													</p>
												) : null}
											</div>
										</li>
									))
								}
							</ul>
							<Button onClick={changeProjectHandler} variant={'primary'} fullWidth disabled={!displayArr?.length}>
							{t("menu.continueButton")}
						</Button>
						</>
					)}
					</>
					)}
				</div>
			</div>
	)
}

export default ProjectNavigationWindow
